import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  IconButton,
  Switch,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import ComputerIcon from '@mui/icons-material/Computer';
import DevicesIcon from '@mui/icons-material/Devices';
import CloseIcon from '@mui/icons-material/Close';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { db, collection, doc, onSnapshot, updateDoc } from '../FirebaseConfig';

const StyledDialog = styled(Dialog)(() => ({
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(8, 9, 16, 0.85)',
    backdropFilter: 'blur(10px)',
  },
  '& .MuiPaper-root': {
    background: 'linear-gradient(180deg, #131525 0%, #0d0e19 100%)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderTop: '3px solid #00d4ff',
    borderRadius: '20px',
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.85), 0 0 30px rgba(0, 212, 255, 0.2)',
    color: '#ffffff',
    width: '100%',
    maxWidth: '650px',
    overflow: 'hidden',
  },
}));

export default function ConnectedDevicesModal({
  open,
  onClose,
  hostId,
  onOpenQr,
}) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMonitorEnabled, setIsMonitorEnabled] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    if (!open || !hostId) return;

    setLoading(true);

    // 1. Escuchar estado maestro de monitores en el documento del host
    const userDocRef = doc(db, 'users', hostId);
    const unsubUser = onSnapshot(userDocRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const enabled = (data.isMonitorEnabled ?? data.configs?.general?.isMonitorEnabled) !== false;
        setIsMonitorEnabled(enabled);
      }
    });

    // 2. Escuchar dispositivos conectados
    const colRef = collection(db, 'users', hostId, 'connected_monitors');
    const unsubDevices = onSnapshot(
      colRef,
      (snapshot) => {
        const now = Date.now();
        const list = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          // Considerar activo si reportó presencia en los últimos 45 segundos
          const lastSeen = data.lastSeen || 0;
          const isAlive = (now - lastSeen) < 45000;
          list.push({
            id: docSnap.id,
            ...data,
            isAlive,
          });
        });

        // Ordenar: primero los activos, luego los más recientes
        list.sort((a, b) => (b.lastSeen || 0) - (a.lastSeen || 0));
        setDevices(list);
        setLoading(false);
      },
      (error) => {
        console.error('Error escuchando dispositivos conectados:', error);
        setLoading(false);
      }
    );

    return () => {
      unsubUser();
      unsubDevices();
    };
  }, [open, hostId]);

  const handleToggleMonitor = async () => {
    if (!hostId || updatingStatus) return;
    setUpdatingStatus(true);
    try {
      const nextState = !isMonitorEnabled;
      setIsMonitorEnabled(nextState);
      const userDocRef = doc(db, 'users', hostId);
      await updateDoc(userDocRef, {
        isMonitorEnabled: nextState,
        'configs.general.isMonitorEnabled': nextState,
      });
    } catch (err) {
      console.error('Error cambiando estado del monitor:', err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const activeCount = devices.filter((d) => d.isAlive).length;

  const formatJoinedTime = (timestamp) => {
    if (!timestamp) return 'Reciente';
    const diffMins = Math.floor((Date.now() - timestamp) / 60000);
    if (diffMins <= 0) return 'Hace un momento';
    if (diffMins === 1) return 'Hace 1 minuto';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    const diffHours = Math.floor(diffMins / 60);
    return `Hace ${diffHours}h`;
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '18px 24px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <DevicesIcon sx={{ color: '#00d4ff', fontSize: '26px' }} />
          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.15rem' }}>
            Monitores Vinculados
          </Typography>
          <Chip
            size="small"
            icon={<FiberManualRecordIcon sx={{ fontSize: '10px !important', color: `${isMonitorEnabled ? '#00d4ff' : '#ffb703'} !important` }} />}
            label={isMonitorEnabled ? `${activeCount} en vivo` : 'En Pausa'}
            sx={{
              backgroundColor: isMonitorEnabled ? 'rgba(0, 212, 255, 0.15)' : 'rgba(255, 183, 3, 0.15)',
              border: `1px solid ${isMonitorEnabled ? 'rgba(0, 212, 255, 0.4)' : 'rgba(255, 183, 3, 0.4)'}`,
              color: isMonitorEnabled ? '#00d4ff' : '#ffb703',
              fontWeight: 700,
              fontSize: '0.75rem',
            }}
          />
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Barra de Control Maestro de Servicio de Monitores */}
      <Box
        sx={{
          padding: '12px 24px',
          background: isMonitorEnabled
            ? 'rgba(0, 212, 255, 0.06)'
            : 'rgba(255, 183, 3, 0.08)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {isMonitorEnabled ? (
            <DevicesIcon sx={{ color: '#00d4ff', fontSize: '20px' }} />
          ) : (
            <WifiOffIcon sx={{ color: '#ffb703', fontSize: '20px' }} />
          )}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
              {isMonitorEnabled ? 'Servicio de Monitores Habilitado' : 'Servicio Temporalmente Pausado'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.72rem' }}>
              {isMonitorEnabled
                ? 'Los invitados sincronizan video y califican en vivo.'
                : 'Los invitados ven pantalla de pausa para optimizar ancho de banda.'}
            </Typography>
          </Box>
        </Box>
        <Tooltip title={isMonitorEnabled ? 'Pausar servicio para todos los monitores' : 'Reanudar servicio de monitores'}>
          <Switch
            checked={isMonitorEnabled}
            onChange={handleToggleMonitor}
            disabled={updatingStatus}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#00d4ff',
                '&:hover': { backgroundColor: 'rgba(0, 212, 255, 0.15)' },
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#00d4ff',
              },
              '& .MuiSwitch-track': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          />
        </Tooltip>
      </Box>

      <DialogContent sx={{ padding: '20px 24px' }}>
        {devices.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              padding: '36px 16px',
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              borderRadius: '16px',
              border: '1px dashed rgba(255, 255, 255, 0.1)',
            }}
          >
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '8px' }}>
              No hay monitores vinculados en este momento
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', display: 'block', marginBottom: '20px' }}>
              Escaneá el código QR desde cualquier celular o tablet para usarlo como monitor sincronizado.
            </Typography>
            {onOpenQr && (
              <Button
                variant="outlined"
                startIcon={<QrCode2Icon />}
                onClick={() => {
                  onClose();
                  onOpenQr();
                }}
                sx={{
                  borderColor: '#00d4ff',
                  color: '#00d4ff',
                  textTransform: 'none',
                  borderRadius: '10px',
                  fontWeight: 700,
                  '&:hover': {
                    borderColor: '#00b4d8',
                    backgroundColor: 'rgba(0, 212, 255, 0.1)',
                  },
                }}
              >
                Ver Código QR
              </Button>
            )}
          </Box>
        ) : (
          <TableContainer
            sx={{
              maxHeight: '380px',
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.06)',
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { backgroundColor: '#181b2e', color: 'rgba(255, 255, 255, 0.6)', fontWeight: 700, fontSize: '0.75rem', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' } }}>
                  <TableCell>DISPOSITIVO</TableCell>
                  <TableCell>TIPO</TableCell>
                  <TableCell>CONECTADO</TableCell>
                  <TableCell align="right">ESTADO</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.map((device) => (
                  <TableRow
                    key={device.id}
                    sx={{
                      '& td': { borderBottom: '1px solid rgba(255, 255, 255, 0.05)', color: '#ffffff', fontSize: '0.82rem' },
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.04)' },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Avatar
                          src={device.photoURL}
                          sx={{
                            width: 32,
                            height: 32,
                            background: 'linear-gradient(135deg, #ff2d9b, #00d4ff)',
                            fontSize: '0.8rem',
                            fontWeight: 700,
                          }}
                        >
                          {(device.displayName || 'I').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                            {device.displayName || 'Invitado en Monitor'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.45)', fontSize: '0.7rem' }}>
                            {device.email || 'Acceso por QR'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'rgba(255, 255, 255, 0.7)' }}>
                        {device.deviceType === 'Móvil' ? (
                          <SmartphoneIcon sx={{ fontSize: '18px', color: '#ff2d9b' }} />
                        ) : (
                          <ComputerIcon sx={{ fontSize: '18px', color: '#00d4ff' }} />
                        )}
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {device.deviceType || 'Móvil'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.75rem' }}>
                      {formatJoinedTime(device.joinedAt)}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        label={device.isAlive ? 'En Vivo' : 'Inactivo'}
                        sx={{
                          height: '22px',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          backgroundColor: device.isAlive ? 'rgba(0, 212, 255, 0.15)' : 'rgba(255, 255, 255, 0.08)',
                          color: device.isAlive ? '#00d4ff' : 'rgba(255, 255, 255, 0.4)',
                          border: device.isAlive ? '1px solid rgba(0, 212, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: '14px 24px', borderTop: '1px solid rgba(255, 255, 255, 0.08)', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>
          Los monitores sincronizan video y votan automáticamente al finalizar cada tema.
        </Typography>
        <Button
          onClick={onClose}
          sx={{
            color: '#ffffff',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            textTransform: 'none',
            borderRadius: '10px',
            fontWeight: 700,
            padding: '6px 16px',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
            },
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}
